import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import BackMethods from "../../back_component/BackMethods";
import {
    FaFileExcel,
    FaSearch,
    FaTable,
    FaCalendarAlt,
    FaFilter,
    FaChartBar,
    FaChevronDown,
    FaChevronUp,
    FaSlidersH,
    FaMapMarkerAlt,
    FaTimes,
    FaCheck,
    FaChartLine,
    FaArrowUp,
    FaHome,
} from "react-icons/fa";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from "recharts";
import "./PriceSurveyMatrixReportPage.css";

const FIXED_COLUMNS = new Set(["productid", "productname", "categoryname"]);

const isMetaColumn = (col) => FIXED_COLUMNS.has(String(col).trim().toLowerCase());

const levelLabels = {
    governorate: "المحافظات",
    city: "المدن",
    district: "القطاعات",
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const addOneDay = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const date = new Date(y, m - 1, d + 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const resolveLevel = ({ governorate, city }) => {
    if (city) return "district";
    if (governorate) return "city";
    return "governorate";
};

const buildMatrixReturnPath = (filterValues) => {
    const params = new URLSearchParams();
    params.set("autoLoad", "1");
    if (filterValues.date) params.set("date", filterValues.date);
    if (filterValues.governorate) params.set("governorate", filterValues.governorate);
    if (filterValues.city) params.set("city", filterValues.city);
    if (filterValues.district) params.set("district", filterValues.district);
    if (filterValues.establishmentUid) params.set("establishmentUid", filterValues.establishmentUid);
    return `/admin/price-survey-matrix-report?${params.toString()}`;
};

const buildFiltersFromMatrixParams = (params, defaultGovernorate = "") => ({
    date: params.get("date") || todayIso(),
    governorate: params.get("governorate") || defaultGovernorate || "",
    city: params.get("city") || "",
    district: params.get("district") || "",
    establishmentUid: params.get("establishmentUid") || "",
});

const cleanText = (value) =>
    String(value ?? "")
        .replace(/\r\n/g, "")
        .trim();

const formatPrice = (value, currency) => {
    if (value == null || value === "") return null;
    const num = Number(value);
    if (Number.isNaN(num)) return null;
    return currency === "USD"
        ? num.toLocaleString("ar-SY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : num.toLocaleString("ar-SY");
};

const customStyles = {
    control: (base) => ({
        ...base,
        borderRadius: "8px",
        minHeight: "42px",
        border: "1px solid #ced4da",
        fontSize: "14px",
    }),
};

const CHART_BAR_FILL = "#1565c0";

const formatChartPrice = (value, currency = "SYP") => {
    const num = Number(value);
    if (Number.isNaN(num)) return "";
    const suffix = currency === "USD" ? "$" : "ل.س";
    return `${num.toLocaleString("ar-SY")} ${suffix}`;
};

const ChartTooltip = ({ active, payload, currency }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload;
    if (!item) return null;
    return (
        <div className="ps-matrix-chart-tooltip">
            <div className="ps-matrix-chart-tooltip__location">{item.location}</div>
            <div className="ps-matrix-chart-tooltip__price">{formatChartPrice(item.price, currency)}</div>
        </div>
    );
};

const createHorizontalBarLabel = (currency) => (props) => {
    const { x, y, width, height, value } = props;
    if (value == null || width < 36) return null;
    return (
        <text
            x={x + width / 2}
            y={y + height / 2}
            fill="#ffffff"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={12}
            fontWeight={700}
        >
            {formatChartPrice(value, currency)}
        </text>
    );
};

const createVerticalBarLabel = (currency) => (props) => {
    const { x, y, width, height, value } = props;
    if (value == null || height < 28) return null;
    return (
        <text
            x={x + width / 2}
            y={y + height / 2}
            fill="#ffffff"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={11}
            fontWeight={700}
        >
            {formatChartPrice(value, currency)}
        </text>
    );
};

export default function PriceSurveyMatrixReportPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { backEnd_request, getUserConstraints } = BackMethods();
    const { userGovernorate, isSuperAdmin } = getUserConstraints();

    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [matrixData, setMatrixData] = useState(null);
    const [locationsData, setLocationsData] = useState([]);
    const [searchProduct, setSearchProduct] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [activeLevel, setActiveLevel] = useState("governorate");
    const [drillDown, setDrillDown] = useState(null);
    const [chartModal, setChartModal] = useState(null);

    const [filters, setFilters] = useState({
        date: todayIso(),
        governorate: isSuperAdmin ? "" : userGovernorate || "",
        city: "",
        district: "",
        establishmentUid: "",
    });

    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await backEnd_request.get("/Locations?includeInactive=false", {
                    params: { size: 10000 },
                });
                setLocationsData(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchLocations();
    }, [backEnd_request]);

    useEffect(() => {
        if (searchParams.get("autoLoad") !== "1") return;
        const defaultGov = isSuperAdmin ? "" : userGovernorate || "";
        const restored = buildFiltersFromMatrixParams(searchParams, defaultGov);
        setFilters(restored);
        fetchReport(restored);
    }, [searchParams]);

    const availableGovernorates = useMemo(
        () => [...new Set(locationsData.map((l) => l.governorate).filter(Boolean))],
        [locationsData]
    );

    const availableCities = useMemo(() => {
        if (!filters.governorate) return [];
        return [
            ...new Set(
                locationsData
                    .filter((l) => l.governorate === filters.governorate)
                    .map((l) => l.city)
                    .filter(Boolean)
            ),
        ];
    }, [locationsData, filters.governorate]);

    const availableDistricts = useMemo(() => {
        if (!filters.governorate || !filters.city) return [];
        return [
            ...new Set(
                locationsData
                    .filter(
                        (l) =>
                            l.governorate === filters.governorate && l.city === filters.city
                    )
                    .map((l) => l.sector)
                    .filter(Boolean)
            ),
        ];
    }, [locationsData, filters.governorate, filters.city]);

    const buildQueryParams = useCallback((filterValues, extra = {}) => {
        const level = resolveLevel(filterValues);
        const params = {
            date: filterValues.date,
            level,
            ...extra,
        };
        if (filterValues.governorate) params.governorate = filterValues.governorate;
        if (filterValues.city) params.city = filterValues.city;
        if (filterValues.district) params.district = filterValues.district;
        if (filterValues.establishmentUid) params.establishmentUid = filterValues.establishmentUid;
        return new URLSearchParams(params).toString();
    }, []);

    const fetchReport = async (overrideFilters = null) => {
        const activeFilters = overrideFilters ? { ...filters, ...overrideFilters } : filters;
        if (!activeFilters.date) return;
        setLoading(true);
        try {
            const level = resolveLevel(activeFilters);
            const res = await backEnd_request.get(
                `/PriceSurveys/matrix/products?${buildQueryParams(activeFilters)}`
            );
            setMatrixData(res.data || null);
            setActiveLevel(level);
            if (overrideFilters) setFilters(activeFilters);
            setSearchProduct("");
            setSelectedCategory("");
            setFiltersOpen(false);
        } catch (err) {
            console.error(err);
            setMatrixData(null);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        if (!matrixData?.rows?.length) return;
        setExporting(true);
        try {
            const response = await backEnd_request.get(
                `/PriceSurveys/matrix/products?${buildQueryParams(filters, { exportXlsx: true })}`,
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `price-survey-matrix-${filters.date}-${activeLevel}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Export error:", err);
        } finally {
            setExporting(false);
        }
    };

    const loadEstablishments = async (inputValue) => {
        if (!inputValue) return [];
        try {
            const res = await backEnd_request.get(
                `/Establishments?q=${inputValue}&page=1&size=20`
            );
            return (res.data?.data || []).map((est) => ({
                value: est.fieldUID,
                label: `${est.name} (${est.district || est.city || est.governorate})`,
            }));
        } catch {
            return [];
        }
    };

    const locationColumns = useMemo(() => {
        if (!matrixData?.columns) return [];
        // أول 3 أعمدة ثابتة: ProductId, ProductName, CategoryName
        return matrixData.columns.filter((col, index) => index >= 3 && !isMetaColumn(col));
    }, [matrixData]);

    const parsedRows = useMemo(() => {
        if (!matrixData?.rows || !matrixData?.columns) return [];

        const colIndex = {};
        matrixData.columns.forEach((col, i) => {
            colIndex[col] = i;
        });

        return matrixData.rows.map((row) => ({
            productId: row[colIndex.ProductId],
            productName: cleanText(row[colIndex.ProductName]),
            categoryName: cleanText(row[colIndex.CategoryName]),
            prices: locationColumns.reduce((acc, loc) => {
                acc[loc] = row[colIndex[loc]];
                return acc;
            }, {}),
        }));
    }, [matrixData, locationColumns]);

    const categories = useMemo(
        () => [...new Set(parsedRows.map((r) => r.categoryName).filter(Boolean))],
        [parsedRows]
    );

    const filteredRows = useMemo(() => {
        return parsedRows.filter((row) => {
            const matchProduct =
                !searchProduct ||
                row.productName.toLowerCase().includes(searchProduct.toLowerCase());
            const matchCategory = !selectedCategory || row.categoryName === selectedCategory;
            return matchProduct && matchCategory;
        });
    }, [parsedRows, searchProduct, selectedCategory]);

    const useScrollLayout = useMemo(() => {
        if (activeLevel === "district") return true;
        if (locationColumns.length > 7) return true;
        return locationColumns.some((col) => col.length > 10);
    }, [activeLevel, locationColumns]);

    const filterSummary = useMemo(() => {
        const parts = [
            new Date(filters.date).toLocaleDateString("ar-SY"),
            levelLabels[activeLevel],
        ];
        if (filters.governorate) parts.push(filters.governorate);
        if (filters.city) parts.push(filters.city);
        if (filters.district) parts.push(filters.district);
        return parts.join(" · ");
    }, [filters, activeLevel]);

    const reportDateLabel = matrixData?.date
        ? new Date(matrixData.date).toLocaleDateString("ar-SY", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : "";

    const canDrillDown = activeLevel === "governorate" || activeLevel === "city";

    const getDrillDownOptions = useCallback(
        (columnName, level) => {
            if (level === "governorate") {
                return [
                    ...new Set(
                        locationsData
                            .filter((l) => l.governorate === columnName)
                            .map((l) => l.city)
                            .filter(Boolean)
                    ),
                ];
            }
            if (level === "city") {
                return [
                    ...new Set(
                        locationsData
                            .filter(
                                (l) =>
                                    l.governorate === filters.governorate &&
                                    l.city === columnName
                            )
                            .map((l) => l.sector)
                            .filter(Boolean)
                    ),
                ];
            }
            return [];
        },
        [locationsData, filters.governorate]
    );

    const handleColumnClick = (columnName) => {
        if (!canDrillDown) return;
        const options = getDrillDownOptions(columnName, activeLevel);
        setDrillDown({
            columnName,
            nextLevel: activeLevel === "governorate" ? "city" : "district",
            options,
        });
    };

    const confirmDrillDown = () => {
        if (!drillDown) return;
        const updates =
            drillDown.nextLevel === "city"
                ? { governorate: drillDown.columnName, city: "", district: "" }
                : { city: drillDown.columnName, district: "" };
        setDrillDown(null);
        fetchReport(updates);
    };

    const handleSectorCellClick = (row, districtName) => {
        if (activeLevel !== "district") return;
        const params = new URLSearchParams();
        params.set("from", filters.date);
        params.set("to", addOneDay(filters.date));
        params.set("productId", String(row.productId));
        if (filters.governorate) params.set("governorate", filters.governorate);
        if (filters.city) params.set("city", filters.city);
        params.set("district", districtName);
        params.set("returnTo", buildMatrixReturnPath(filters));
        navigate(`/admin/price-survey-report?${params.toString()}`);
    };

    const handlePriceCellClick = (row, loc) => {
        if (activeLevel === "district") {
            handleSectorCellClick(row, loc);
            return;
        }
        if (canDrillDown) handleColumnClick(loc);
    };

    const handleProductChartClick = (row) => {
        const data = locationColumns
            .map((loc) => ({
                location: loc,
                price: row.prices[loc] != null ? Number(row.prices[loc]) : null,
            }))
            .filter((item) => item.price != null && !Number.isNaN(item.price));

        setChartModal({
            productName: row.productName,
            categoryName: row.categoryName,
            data,
        });
    };

    const chartUseHorizontal = (chartModal?.data?.length ?? 0) > 6 || activeLevel === "district";

    const chartYAxisWidth = useMemo(() => {
        if (!chartModal?.data?.length) return 140;
        return Math.min(
            Math.max(...chartModal.data.map((d) => d.location.length * 10), 140),
            300
        );
    }, [chartModal?.data]);

    const drillDownTitle =
        drillDown?.nextLevel === "city"
            ? `عرض مدن محافظة ${drillDown.columnName}`
            : `عرض قطاعات مدينة ${drillDown?.columnName}`;

    const drillDownHint =
        drillDown?.nextLevel === "city"
            ? "سيتم عرض أسعار المواد على مستوى المدن داخل هذه المحافظة"
            : "سيتم عرض أسعار المواد على مستوى القطاعات داخل هذه المدينة";

    const upLevelAction = useMemo(() => {
        if (!matrixData) return null;

        if (activeLevel === "district" && filters.city) {
            return {
                label: "رجوع إلى المدن",
                hint: `محافظة ${filters.governorate}`,
                updates: { city: "", district: "" },
            };
        }

        if (activeLevel === "city" && filters.governorate) {
            if (!isSuperAdmin && userGovernorate) return null;
            return {
                label: "رجوع إلى المحافظات",
                hint: filters.governorate,
                updates: { governorate: "", city: "", district: "" },
            };
        }

        return null;
    }, [
        matrixData,
        activeLevel,
        filters.governorate,
        filters.city,
        isSuperAdmin,
        userGovernorate,
    ]);

    const handleGoUpLevel = () => {
        if (!upLevelAction || loading) return;
        fetchReport(upLevelAction.updates);
    };

    return (
        <div
            className={`container-fluid px-3 px-md-4 py-4 ps-matrix-page ${!filtersOpen && matrixData ? "ps-matrix-filters-collapsed" : ""}`}
            dir="rtl"
        >
            {/* Header */}
            <div className="ps-matrix-header mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                <div className="d-flex align-items-start gap-3 flex-wrap">
                    <button
                        type="button"
                        className="btn btn-outline-light btn-sm fw-bold d-flex align-items-center shadow-sm flex-shrink-0"
                        onClick={() => navigate("/admin")}
                    >
                        <FaHome className="ms-2" />
                        عودة للرئيسية
                    </button>
                    <div>
                    <h3>
                        <FaTable className="ms-2" />
                        تقرير سبر الأسعار — مصفوفة يومية
                    </h3>
                    <p className="mb-0 mt-1 opacity-75 text-white">
                        {matrixData
                                ? `عرض على مستوى ${levelLabels[activeLevel]} — اضغط على المادة للرسم البياني${activeLevel === "district" ? " أو على خلية للتفاصيل" : ""}`
                            : "اختر التاريخ والموقع ثم اضغط عرض التقرير"}
                    </p>
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    {matrixData && (
                        <>
                            <span className="meta-badge">
                                <FaCalendarAlt className="ms-1" />
                                {reportDateLabel}
                            </span>
                            <span className="meta-badge">
                                <FaChartBar className="ms-1" />
                                {filteredRows.length} مادة
                            </span>
                        </>
                    )}
                    <button
                        type="button"
                        className="btn btn-light btn-sm fw-bold shadow-sm d-flex align-items-center"
                        disabled={!matrixData?.rows?.length || exporting}
                        onClick={handleExport}
                    >
                        {exporting ? (
                            <span className="spinner-border spinner-border-sm ms-2" />
                        ) : (
                            <FaFileExcel className="ms-2 text-success" />
                        )}
                        تصدير Excel
                    </button>
                </div>
            </div>

            {/* Filters — collapsed summary */}
            {!filtersOpen && (
                <div className="ps-matrix-filters-summary mb-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <FaSlidersH className="text-primary" />
                            <span className="fw-bold small text-secondary">الفلاتر:</span>
                            <span className="small">{filterSummary}</span>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-primary btn-sm fw-bold d-flex align-items-center"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <FaChevronDown className="ms-1" />
                            تعديل الفلاتر
                        </button>
                    </div>
                </div>
            )}

            {/* Filters — expanded */}
            {filtersOpen && (
                <div className="ps-matrix-filters p-4 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold text-secondary mb-0">
                            <FaFilter className="ms-2 text-primary" />
                            خيارات البحث
                        </h6>
                        {matrixData && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-decoration-none p-0"
                                onClick={() => setFiltersOpen(false)}
                            >
                                <FaChevronUp className="ms-1" />
                                إخفاء
                            </button>
                        )}
                    </div>

                    <div className="row g-3">
                        <div className="col-6 col-md-3 col-lg-2">
                            <label className="text-danger">
                                <FaCalendarAlt className="ms-1" />
                                التاريخ *
                            </label>
                            <input
                                type="date"
                                className="form-control"
                                value={filters.date}
                                onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                            />
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label>المحافظة</label>
                            <select
                                className="form-select"
                                value={filters.governorate}
                                disabled={!isSuperAdmin && !!userGovernorate}
                                onChange={(e) =>
                                    setFilters({
                                        ...filters,
                                        governorate: e.target.value,
                                        city: "",
                                        district: "",
                                    })
                                }
                            >
                                <option value="">كل المحافظات</option>
                                {availableGovernorates.map((g) => (
                                    <option key={g} value={g}>
                                        {g}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label>المدينة</label>
                            <select
                                className="form-select"
                                value={filters.city}
                                disabled={!filters.governorate}
                                onChange={(e) =>
                                    setFilters({ ...filters, city: e.target.value, district: "" })
                                }
                            >
                                <option value="">كل المدن</option>
                                {availableCities.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-6 col-md-3 col-lg-2">
                            <label>القطاع</label>
                            <select
                                className="form-select"
                                value={filters.district}
                                disabled={!filters.city}
                                onChange={(e) =>
                                    setFilters({ ...filters, district: e.target.value })
                                }
                            >
                                <option value="">كل القطاعات</option>
                                {availableDistricts.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="col-12 col-md-6 col-lg-2">
                            <label>المنشأة (اختياري)</label>
                            <AsyncSelect
                                cacheOptions
                                loadOptions={loadEstablishments}
                                placeholder="بحث عن منشأة..."
                                styles={customStyles}
                                isClearable
                                onChange={(opt) =>
                                    setFilters({ ...filters, establishmentUid: opt?.value || "" })
                                }
                            />
                        </div>

                        <div className="col-12 col-md-6 col-lg-2 d-flex align-items-end">
                            <button
                                type="button"
                                className="btn btn-primary w-100 fw-bold shadow-sm"
                                style={{ height: "42px" }}
                                disabled={!filters.date || loading}
                                onClick={fetchReport}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    <>
                                        <FaSearch className="ms-2" />
                                        عرض التقرير
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="alert alert-info py-2 px-3 mt-3 mb-0 small">
                        المستوى يُحدَّد تلقائياً: بدون محافظة → المحافظات، مع محافظة → المدن، مع
                        مدينة → القطاعات
                    </div>
                </div>
            )}

            {/* Toolbar */}
            {matrixData?.rows?.length > 0 && (
                <div className="ps-matrix-toolbar mb-3 d-flex flex-column flex-md-row gap-2 align-items-md-center">
                    <div className="flex-grow-1">
                        <div className="input-group">
                            <span className="input-group-text bg-white">
                                <FaFilter className="text-muted" />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="بحث في اسم المادة..."
                                value={searchProduct}
                                onChange={(e) => setSearchProduct(e.target.value)}
                            />
                        </div>
                    </div>
                    <div style={{ minWidth: "200px" }}>
                        <select
                            className="form-select"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="">كل التصنيفات</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Matrix Table */}
            {matrixData?.rows?.length > 0 && filteredRows.length > 0 && (
                <div className="ps-matrix-table-wrap">
                    {useScrollLayout && (
                        <div className="ps-matrix-scroll-hint">
                            ← مرّر أفقياً لعرض جميع {levelLabels[activeLevel]} →
                        </div>
                    )}
                    <div className={`ps-matrix-scroll ${useScrollLayout ? "ps-matrix-scroll--wide" : ""}`}>
                        <table className={`ps-matrix-table ${useScrollLayout ? "ps-matrix-table--scroll" : ""}`}>
                            <colgroup>
                                <col className="col-product-width" />
                                {locationColumns.map((col) => (
                                    <col key={col} className="col-price-width" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th className="col-product">المادة</th>
                                    {locationColumns.map((col) => (
                                        <th
                                            key={col}
                                            className={canDrillDown ? "col-drilldown" : ""}
                                            onClick={() => handleColumnClick(col)}
                                            title={col}
                                            style={
                                                useScrollLayout
                                                    ? { minWidth: `${Math.min(Math.max(col.length * 8, 90), 220)}px` }
                                                    : undefined
                                            }
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((row, idx) => (
                                    <tr key={`${row.productId}-${idx}`}>
                                        <td
                                            className="col-product col-product-chart"
                                            title={`${row.productName} — اضغط لعرض الرسم البياني`}
                                            onClick={() => handleProductChartClick(row)}
                                        >
                                            {/* <FaChartLine className="col-product-chart__icon" /> */}
                                            {row.productName}
                                        </td>
                                        {locationColumns.map((loc) => {
                                            const raw = row.prices[loc];
                                            const formatted = formatPrice(raw, matrixData.currency);
                                            return (
                                                <td
                                                    key={loc}
                                                    className={`price-cell ${formatted ? "has-value" : "no-value"} ${canDrillDown || activeLevel === "district" ? "col-drilldown" : ""} ${activeLevel === "district" && formatted ? "col-detail-link" : ""}`}
                                                    onClick={() => handlePriceCellClick(row, loc)}
                                                    title={
                                                        activeLevel === "district"
                                                            ? formatted
                                                                ? `عرض تفاصيل سبر ${row.productName} في ${loc}`
                                                                : `عرض تفاصيل ${row.productName} في ${loc}`
                                                            : canDrillDown
                                                              ? `اضغط لعرض ${activeLevel === "governorate" ? "مدن" : "قطاعات"} ${loc}`
                                                              : formatted
                                                                ? `${row.productName} — ${loc}: ${formatted}`
                                                                : "لا توجد بيانات"
                                                    }
                                                >
                                                    {formatted ?? "—"}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Empty states */}
            {!loading && !matrixData && (
                <div className="ps-matrix-empty">
                    <div className="icon">
                        <FaTable />
                    </div>
                    <h5 className="fw-bold text-secondary">ابدأ باختيار التاريخ وعرض التقرير</h5>
                    <p className="text-muted small mb-0">
                        اختر تاريخ السبر والموقع الجغرافي ثم اضغط «عرض التقرير»
                    </p>
                </div>
            )}

            {!loading && matrixData && (!matrixData.rows || matrixData.rows.length === 0) && (
                <div className="ps-matrix-empty">
                    <div className="icon">
                        <FaSearch />
                    </div>
                    <h5 className="fw-bold text-secondary">لا توجد بيانات لهذا اليوم</h5>
                    <p className="text-muted small mb-0">
                        جرّب تاريخاً آخر أو غيّر الفلاتر الجغرافية
                    </p>
                </div>
            )}

            {matrixData?.rows?.length > 0 && filteredRows.length === 0 && (
                <div className="ps-matrix-empty mt-3">
                    <h5 className="fw-bold text-secondary">لا توجد مواد مطابقة للبحث</h5>
                    <p className="text-muted small mb-0">جرّب تعديل كلمات البحث أو التصنيف</p>
                </div>
            )}

            {/* Product chart modal */}
            {chartModal && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setChartModal(null)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-chart-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaChartLine className="text-primary" />
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold">{chartModal.productName}</h6>
                                {chartModal.categoryName && (
                                    <small className="text-muted">{chartModal.categoryName}</small>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setChartModal(null)}
                                aria-label="إغلاق"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-chart-modal__body">
                            <p className="text-muted small mb-3">
                                أسعار المادة في {reportDateLabel} حسب {levelLabels[activeLevel]}
                                {matrixData?.currency && (
                                    <span className="fw-bold">
                                        {" "}
                                        ({matrixData.currency === "USD" ? "$" : "ل.س"})
                                    </span>
                                )}
                            </p>
                            {chartModal.data.length > 0 ? (
                                <>
                                    <div
                                        className="ps-matrix-chart-wrap"
                                        style={{
                                            height: chartUseHorizontal
                                                ? Math.min(Math.max(chartModal.data.length * 52, 360), 680)
                                                : 400,
                                        }}
                                    >
                                        <ResponsiveContainer width="100%" height="100%">
                                            {chartUseHorizontal ? (
                                                <BarChart
                                                    layout="vertical"
                                                    data={chartModal.data}
                                                    margin={{
                                                        top: 12,
                                                        right: 24,
                                                        left: chartYAxisWidth + 8,
                                                        bottom: 12,
                                                    }}
                                                    barCategoryGap="22%"
                                                >
                                                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" horizontal={false} />
                                                    <XAxis
                                                        type="number"
                                                        tick={{ fontSize: 13, fill: "#334155", fontWeight: 600 }}
                                                        axisLine={{ stroke: "#94a3b8" }}
                                                        tickLine={{ stroke: "#94a3b8" }}
                                                        tickFormatter={(v) => v.toLocaleString("ar-SY")}
                                                    />
                                                    <YAxis
                                                        type="category"
                                                        dataKey="location"
                                                        width={chartYAxisWidth}
                                                        orientation="left"
                                                        tick={{
                                                            fontSize: 13,
                                                            fill: "#0f172a",
                                                            fontWeight: 700,
                                                        }}
                                                        tickMargin={10}
                                                        axisLine={{ stroke: "#94a3b8" }}
                                                        tickLine={false}
                                                    />
                                                    <Tooltip
                                                        content={
                                                            <ChartTooltip currency={matrixData?.currency} />
                                                        }
                                                        cursor={{ fill: "rgba(21, 101, 192, 0.06)" }}
                                                    />
                                                    <Bar
                                                        dataKey="price"
                                                        fill={CHART_BAR_FILL}
                                                        radius={[0, 6, 6, 0]}
                                                        barSize={28}
                                                        minPointSize={8}
                                                    >
                                                        <LabelList
                                                            dataKey="price"
                                                            content={createHorizontalBarLabel(matrixData?.currency)}
                                                        />
                                                    </Bar>
                                                </BarChart>
                                            ) : (
                                                <BarChart
                                                    data={chartModal.data}
                                                    margin={{ top: 28, right: 20, left: 12, bottom: 80 }}
                                                    barCategoryGap="25%"
                                                >
                                                    <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
                                                    <XAxis
                                                        dataKey="location"
                                                        tick={{ fontSize: 13, fill: "#0f172a", fontWeight: 600 }}
                                                        interval={0}
                                                        angle={-30}
                                                        textAnchor="end"
                                                        height={90}
                                                        axisLine={{ stroke: "#94a3b8" }}
                                                        tickLine={{ stroke: "#94a3b8" }}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 13, fill: "#334155", fontWeight: 600 }}
                                                        axisLine={{ stroke: "#94a3b8" }}
                                                        tickLine={{ stroke: "#94a3b8" }}
                                                        tickFormatter={(v) => v.toLocaleString("ar-SY")}
                                                        width={70}
                                                    />
                                                    <Tooltip
                                                        content={
                                                            <ChartTooltip currency={matrixData?.currency} />
                                                        }
                                                        cursor={{ fill: "rgba(21, 101, 192, 0.06)" }}
                                                    />
                                                    <Bar
                                                        dataKey="price"
                                                        fill={CHART_BAR_FILL}
                                                        radius={[6, 6, 0, 0]}
                                                        barSize={48}
                                                        minPointSize={8}
                                                    >
                                                        <LabelList
                                                            dataKey="price"
                                                            content={createVerticalBarLabel(matrixData?.currency)}
                                                        />
                                                    </Bar>
                                                </BarChart>
                                            )}
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="ps-matrix-chart-table-wrap mt-3">
                                        <table className="table table-sm table-bordered mb-0 ps-matrix-chart-table">
                                            <thead>
                                                <tr>
                                                    <th>{levelLabels[activeLevel]}</th>
                                                    <th className="text-center">السعر</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {chartModal.data.map((item) => (
                                                    <tr key={item.location}>
                                                        <td className="fw-semibold">{item.location}</td>
                                                        <td className="text-center fw-bold text-success">
                                                            {formatChartPrice(item.price, matrixData?.currency)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-warning py-3 mb-0 text-center small">
                                    لا توجد أسعار مسجّلة لهذه المادة في المواقع المعروضة
                                </div>
                            )}
                        </div>
                        <div className="ps-matrix-drill-modal__footer">
                            <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold"
                                onClick={() => setChartModal(null)}
                            >
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Drill-down modal */}
            {drillDown && (
                <div
                    className="ps-matrix-drill-overlay"
                    onClick={() => setDrillDown(null)}
                    role="presentation"
                >
                    <div
                        className="ps-matrix-drill-modal"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="ps-matrix-drill-modal__header">
                            <FaMapMarkerAlt className="text-primary" />
                            <h6 className="mb-0 fw-bold">{drillDownTitle}</h6>
                            <button
                                type="button"
                                className="btn btn-sm btn-light border-0"
                                onClick={() => setDrillDown(null)}
                                aria-label="إغلاق"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="ps-matrix-drill-modal__body">
                            <p className="text-muted small mb-3">{drillDownHint}</p>
                            {drillDown.options.length > 0 ? (
                                <>
                                    <p className="fw-bold small mb-2">
                                        {drillDown.nextLevel === "city"
                                            ? "المدن المتاحة:"
                                            : "القطاعات المتاحة:"}
                                    </p>
                                    <div className="ps-matrix-drill-tags">
                                        {drillDown.options.map((item) => (
                                            <span key={item} className="ps-matrix-drill-tag">
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="alert alert-warning py-2 px-3 mb-0 small">
                                    لا توجد{" "}
                                    {drillDown.nextLevel === "city" ? "مدن" : "قطاعات"} مسجّلة لهذا
                                    الموقع، يمكنك المتابعة على أي حال.
                                </div>
                            )}
                        </div>
                        <div className="ps-matrix-drill-modal__footer">
                            <button
                                type="button"
                                className="btn btn-light btn-sm fw-bold"
                                onClick={() => setDrillDown(null)}
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm fw-bold"
                                disabled={loading}
                                onClick={confirmDrillDown}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" />
                                ) : (
                                    <>
                                        <FaCheck className="ms-1" />
                                        تأكيد وعرض
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {upLevelAction && !drillDown && !chartModal && (
                <button
                    type="button"
                    className="ps-matrix-level-back"
                    onClick={handleGoUpLevel}
                    disabled={loading}
                    title={upLevelAction.hint}
                >
                    {loading ? (
                        <span className="spinner-border spinner-border-sm" />
                    ) : (
                        <FaArrowUp />
                    )}
                    <span className="ps-matrix-level-back__text">
                        <span className="ps-matrix-level-back__label">{upLevelAction.label}</span>
                        <span className="ps-matrix-level-back__hint">{upLevelAction.hint}</span>
                    </span>
                </button>
            )}
        </div>
    );
}
